import {
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Avatar,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
  ToggleOnOutlined as ToggleOnIcon,
  ToggleOffOutlined as ToggleOffIcon,
  StorefrontOutlined as MarketplaceIcon,
} from "@mui/icons-material";
import {useCallback, useEffect, useRef, useState} from "react";
import {
  getPartnerships,
  deletePartnership,
  togglePartnershipStatus,
  getMarketplaceOpen,
  setMarketplaceOpen,
} from "../services/PartnershipService";
import PartnershipDialog, {
  CATEGORY_OPTIONS,
  BADGE_META,
} from "../componenets/partnerships/PartnershipDialog";
import {FilterDropdown} from "../componenets/ui";
import {t} from "../componenets/dashboard/coach/coachTokens";
import {useMessageContext} from "../contexts/MessageContext";

/* ── Table cell presets (match Guides / Clients / Exercises) ── */
const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

const categoryLabel = (value) =>
  CATEGORY_OPTIONS.find((c) => c.value === value)?.label || value;

const CATEGORY_OPTS = [
  {value: "ALL", label: "כל הקטגוריות"},
  ...CATEGORY_OPTIONS.map((c) => ({value: c.value, label: c.label})),
];

const AdminPartnerships = () => {
  const {notificationHandler} = useMessageContext();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPartnership, setEditPartnership] = useState(null);

  const [partnerships, setPartnerships] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [category, setCategory] = useState("");
  const [marketplaceOpen, setMarketplaceOpenState] = useState(false);
  const [togglingOpen, setTogglingOpen] = useState(false);

  // Debounced search
  const [searchInput, setSearchInput] = useState("");
  const debounceRef = useRef(null);
  const handleSearch = useCallback((val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setKeyword(val), 300);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    getPartnerships(0, 100, keyword, category, null)
      .then((res) => {
        setPartnerships(res.data?.content || []);
        setTotal(res.data?.totalElements ?? res.data?.content?.length ?? 0);
      })
      .catch((e) => {
        console.error("Error loading partnerships:", e);
        notificationHandler.error("טעינת השותפים נכשלה");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, category]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getMarketplaceOpen()
      .then((res) => setMarketplaceOpenState(!!res.data))
      .catch((e) => console.error("Error loading marketplace status:", e));
  }, []);

  const handleToggleMarketplace = async (open) => {
    setTogglingOpen(true);
    try {
      const res = await setMarketplaceOpen(open);
      setMarketplaceOpenState(!!res.data);
      notificationHandler.success(open ? "המרקטפלייס נפתח למשתמשים" : "המרקטפלייס נסגר");
    } catch (e) {
      console.error("Error toggling marketplace:", e);
      notificationHandler.error("העדכון נכשל");
    } finally {
      setTogglingOpen(false);
    }
  };

  const openCreate = () => {
    setEditPartnership(null);
    setDialogOpen(true);
  };

  const openEdit = (partnership) => (e) => {
    if (e) e.stopPropagation();
    setEditPartnership(partnership);
    setDialogOpen(true);
  };

  const handleToggle = (partnership) => async (e) => {
    if (e) e.stopPropagation();
    try {
      await togglePartnershipStatus(partnership.id, !partnership.enabled);
      notificationHandler.success(partnership.enabled ? "השותף הושבת" : "השותף הופעל");
      load();
    } catch (err) {
      console.error("Error toggling partnership:", err);
      notificationHandler.error("העדכון נכשל");
    }
  };

  const handleDelete = (partnership) => async (e) => {
    if (e) e.stopPropagation();
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`למחוק את "${partnership.name}"?`)) {
      return;
    }
    try {
      await deletePartnership(partnership.id);
      notificationHandler.success("השותף נמחק");
      load();
    } catch (err) {
      console.error("Error deleting partnership:", err);
      notificationHandler.error("המחיקה נכשלה");
    }
  };

  return (
    <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>
      <PartnershipDialog
        isOpen={dialogOpen}
        close={() => setDialogOpen(false)}
        setReload={load}
        editPartnership={editPartnership}
      />

      {/* ─── Title row (on canvas) ─── */}
      <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
        <Box>
          <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
            ניהול שותפים
          </Typography>
          <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>
            ניהול שותפי המרקטפלייס וההטבות שלהם
          </Typography>
        </Box>
        <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
          {/* Marketplace open/closed switch */}
          <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
            <Box component="span" sx={{
              fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: "20px", whiteSpace: "nowrap",
              background: marketplaceOpen ? t.greenLight : t.redLight,
              color: marketplaceOpen ? t.greenText : t.redText,
              border: `1px solid ${marketplaceOpen ? "rgba(1,187,167,.2)" : "rgba(224,62,62,.2)"}`,
            }}>
              {marketplaceOpen ? "המרקטפלייס פתוח" : "המרקטפלייס סגור"}
            </Box>
            <Switch
              checked={marketplaceOpen}
              disabled={togglingOpen}
              onChange={(e) => handleToggleMarketplace(e.target.checked)}
              size="small"
            />
          </Box>

          <Box component="button" onClick={openCreate} sx={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            background: t.green, color: "#fff", height: 34, px: "15px", borderRadius: t.rs,
            fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
            border: "none", cursor: "pointer", fontFamily: "inherit", transition: "all .14s",
            "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
          }}>
            <AddIcon sx={{fontSize: 16}}/>
            שותף חדש
          </Box>
        </Box>
      </Box>

      {/* ─── Card ─── */}
      <Box sx={{background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r, boxShadow: t.sh, overflow: "hidden", display: "flex", flexDirection: "column"}}>

        {/* Filter bar */}
        <Box sx={{
          display: "flex", alignItems: "center", gap: "7px",
          padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
          background: t.bg, flexWrap: "wrap", flexShrink: 0, direction: "rtl",
        }}>
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            fontSize: "12px", fontWeight: 700, color: t.greenText,
            background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
            padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
          }}>
            <MarketplaceIcon sx={{fontSize: 14}}/>
            {total} שותפים
          </Box>
          <Box sx={{position: "relative"}}>
            <SearchIcon sx={{position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none"}}/>
            <InputBase
              value={searchInput}
              onChange={e => handleSearch(e.target.value)}
              placeholder="חיפוש שותף..."
              sx={{
                width: 220, padding: "6px 10px 6px 30px",
                border: `1.5px solid ${t.border}`, borderRadius: t.rs,
                background: t.white, color: t.text, fontSize: "12px",
                "& input": {padding: 0},
                "& input::placeholder": {color: t.t3, opacity: 1},
                "&:focus-within": {borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)"},
              }}
            />
          </Box>
          <FilterDropdown
            label="קטגוריה"
            value={category || "ALL"}
            onChange={v => setCategory(v === "ALL" ? "" : v)}
            options={CATEGORY_OPTS}
            minWidth={150}
          />
        </Box>

        {/* Table area */}
        {loading && !partnerships.length ? (
          <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 540}}>
            <CircularProgress size={32} sx={{color: t.green}}/>
          </Box>
        ) : !partnerships.length ? (
          <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px"}}>
            <MarketplaceIcon sx={{fontSize: 48, color: t.border}}/>
            <Typography sx={{color: t.t3, fontSize: "15px", fontWeight: 500}}>אין שותפים</Typography>
            <Typography sx={{color: t.t3, fontSize: "13px"}}>לחץ על "שותף חדש" כדי להוסיף</Typography>
          </Box>
        ) : (
          <TableContainer sx={{overflowY: "auto", maxHeight: 600, direction: "rtl"}}>
            <Table stickyHeader sx={{tableLayout: "fixed"}}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{...th, textAlign: "right"}}>שותף</TableCell>
                  <TableCell sx={{...th, textAlign: "center", width: "13%", display: {xs: "none", md: "table-cell"}}}>קטגוריה</TableCell>
                  <TableCell sx={{...th, textAlign: "center", width: "16%", display: {xs: "none", md: "table-cell"}}}>הטבה</TableCell>
                  <TableCell sx={{...th, textAlign: "center", width: "18%", display: {xs: "none", lg: "table-cell"}}}>אימייל</TableCell>
                  <TableCell sx={{...th, textAlign: "center", width: "10%"}}>סטטוס</TableCell>
                  <TableCell sx={{...th, textAlign: "center", width: 120}}>פעולות</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {partnerships.map(p => (
                  <TableRow key={p.id}
                            sx={{cursor: "pointer", "&:hover td": {background: t.greenLight}, transition: "background .12s ease"}}
                            onClick={openEdit(p)}>
                    {/* Name + logo + tagline */}
                    <TableCell sx={{...td, textAlign: "right"}}>
                      <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                        <Avatar src={p.logoUrl || undefined} variant="rounded"
                                sx={{width: 36, height: 36, bgcolor: t.bg, color: t.t2, fontSize: 14, fontWeight: 700}}>
                          {p.name?.charAt(0)}
                        </Avatar>
                        <Box sx={{minWidth: 0}}>
                          <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                            <Typography sx={{fontSize: "12.5px", fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                              {p.name}
                            </Typography>
                            {p.badge && BADGE_META[p.badge] && (
                              <Box component="span" sx={{
                                marginInlineStart: "auto",
                                fontSize: 9.5, fontWeight: 700, padding: "1px 7px", borderRadius: "10px",
                                whiteSpace: "nowrap", lineHeight: 1.6,
                                bgcolor: BADGE_META[p.badge].bg, color: BADGE_META[p.badge].color,
                              }}>
                                {BADGE_META[p.badge].label}
                              </Box>
                            )}
                          </Box>
                          {p.tagline && (
                            <Typography sx={{fontSize: "11.5px", color: t.t2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 360}}>
                              {p.tagline}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Category */}
                    <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                      <Box component="span" sx={{
                        display: "inline-block", fontSize: 11, fontWeight: 600, padding: "2px 10px",
                        borderRadius: "20px", background: t.bg, color: t.t2, border: `1px solid ${t.border}`, whiteSpace: "nowrap",
                      }}>
                        {categoryLabel(p.category)}
                      </Box>
                    </TableCell>

                    {/* Offer */}
                    <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                      {p.offer ? (
                        <Typography sx={{fontSize: "12px", fontWeight: 700, color: t.greenText, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                          {p.offer}
                        </Typography>
                      ) : <Typography sx={{fontSize: "12.5px", color: t.t3}}>—</Typography>}
                    </TableCell>

                    {/* Email */}
                    <TableCell sx={{...td, textAlign: "center", display: {xs: "none", lg: "table-cell"}}}>
                      <Typography sx={{fontSize: "12px", color: t.t2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        {p.contactEmail}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell sx={{...td, textAlign: "center"}}>
                      <Box component="span" sx={{
                        display: "inline-block", fontSize: 11, fontWeight: 700, padding: "2px 12px", borderRadius: "20px", whiteSpace: "nowrap",
                        background: p.enabled ? t.greenLight : t.bg,
                        color: p.enabled ? t.greenText : t.t3,
                        border: `1px solid ${p.enabled ? "rgba(1,187,167,.2)" : t.border}`,
                      }}>
                        {p.enabled ? "פעיל" : "מושבת"}
                      </Box>
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={{...td, textAlign: "center", width: 120}} onClick={(e) => e.stopPropagation()}>
                      <Box sx={{display: "inline-flex", justifyContent: "center", gap: "2px"}}>
                        <Tooltip title="עריכה">
                          <IconButton size="small" onClick={openEdit(p)} sx={{color: t.t2, "&:hover": {color: t.green, bgcolor: t.greenLight}}}>
                            <EditIcon fontSize="small"/>
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={p.enabled ? "השבת" : "הפעל"}>
                          <IconButton size="small" onClick={handleToggle(p)} sx={{color: p.enabled ? t.green : t.t2, "&:hover": {bgcolor: t.greenLight}}}>
                            {p.enabled ? <ToggleOnIcon fontSize="small"/> : <ToggleOffIcon fontSize="small"/>}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="מחק">
                          <IconButton size="small" onClick={handleDelete(p)} sx={{color: t.red, "&:hover": {bgcolor: t.redLight}}}>
                            <DeleteIcon fontSize="small"/>
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default AdminPartnerships;
